import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchAndSetUser } from "../../../utils/refreshSessionData";
import AgentLayout from "../components/AgentLayout";
import NavButton from "../components/NavButton";
import Bootstrap4DatePickerComponent from "../../client/components/Bootstrap4DatePickerComponent";
import axiosInstance from "../../../utils/axiosInstance";
import { getMediaUrl } from "../../../utils/mediaUrl";
import "../../client/components/jquery-global";
import $ from "jquery";
import JSZip from "jszip";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import "datatables.net-dt";
import "datatables.net-bs4/js/dataTables.bootstrap4.js";
import "datatables.net-bs4/css/dataTables.bootstrap4.css";
import "datatables.net-responsive-bs4/js/responsive.bootstrap4.js";
import "datatables.net-responsive-bs4/css/responsive.bootstrap4.css";
import "datatables.net-buttons-dt";
import "datatables.net-buttons/js/buttons.html5";
import "datatables.net-buttons/js/buttons.print";
import "datatables.net-buttons-dt/css/buttons.dataTables.css";

window.JSZip = JSZip;
pdfMake.vfs = pdfFonts.vfs || pdfFonts;
if (typeof pdfMake.addVirtualFileSystem === "function") {
  pdfMake.addVirtualFileSystem(pdfFonts);
}
window.pdfMake = pdfMake;

function toLocalString(d) {
  const pad = (x) => String(x).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

function formatDisplayId(displayId) {
  return displayId != null ? `deposit-${displayId}` : "";
}

function escapeHtml(str = "") {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

const STATUS_TABS = [
  { status: 0, label: "Pending Deposit", filterTitle: "Pending Deposits", listTitle: "Pending Deposit List" },
  { status: 1, label: "Approved Deposit", filterTitle: "Approved Deposits", listTitle: "Approved Deposit List" },
  { status: 2, label: "Cancel Deposit", filterTitle: "Cancel Deposits", listTitle: "Cancel Deposit List" },
];

const EXPORT_COLUMNS = ":not(.no-export)";
const AUTO_REFRESH_MS = 3 * 60 * 1000;

const DepositRequest = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userStore);
  const userInitialized = userInfo.initialized;
  const allowAutoDeposit = !!userInfo.data?.allow_auto_deposit;

  const today = new Date();
  const [startDate, setStartDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0)
  );
  const [endDate, setEndDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)
  );
  const [activeStatus, setActiveStatus] = useState(0);
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", isError: false });
  const [gridKey, setGridKey] = useState(Date.now());

  const tableRef = useRef(null);
  const handleDepositActionRef = useRef(null);
  const actionLoadingRef = useRef(false);
  const fetchingRef = useRef(false);

  const activeTabConfig =
    STATUS_TABS.find((tab) => tab.status === activeStatus) || STATUS_TABS[0];

  useEffect(() => {
    if (!userInitialized) return;
    if (!allowAutoDeposit) {
      navigate("/agent/accounts/chart");
    }
  }, [userInitialized, allowAutoDeposit, navigate]);

  const fetchDepositRequests = useCallback(async ({ silent = false } = {}) => {
    if (fetchingRef.current && silent) return;
    fetchingRef.current = true;
    if (!silent) setIsLoading(true);
    try {
      const response = await axiosInstance.get("/company/deposit-requests", {
        params: {
          status: activeStatus,
          from: toLocalString(startDate),
          to: toLocalString(endDate),
        },
      });
      setRecords(response.data?.data || []);
      setGridKey(Date.now());
    } catch (error) {
      setRecords([]);
      setGridKey(Date.now());
      setMessage({
        text:
          error?.response?.data?.message ||
          "Failed to load deposit requests",
        isError: true,
      });
    } finally {
      fetchingRef.current = false;
      if (!silent) setIsLoading(false);
    }
  }, [activeStatus, startDate, endDate]);

  useEffect(() => {
    if (!allowAutoDeposit || !userInitialized) return;
    fetchDepositRequests();
    const intervalId = setInterval(() => {
      if (document.hidden || actionLoadingRef.current) return;
      fetchDepositRequests({ silent: true });
    }, AUTO_REFRESH_MS);
    return () => clearInterval(intervalId);
  }, [allowAutoDeposit, userInitialized, fetchDepositRequests]);

  const handleDepositAction = useCallback(
    async (id, action) => {
      const confirmText =
        action === "approve"
          ? "Approve this deposit and credit the user?"
          : "Cancel this deposit request?";
      if (!window.confirm(confirmText)) return;

      setActionLoading(true);
      actionLoadingRef.current = true;
      try {
        const response = await axiosInstance.post("/company/deposit-request-action", {
          id,
          action,
        });
        setMessage({
          text: response.data?.message || "Updated successfully",
          isError: false,
        });
        fetchDepositRequests();
        fetchAndSetUser(dispatch);
      } catch (error) {
        setMessage({
          text:
            error?.response?.data?.message ||
            "Failed to update deposit request",
          isError: true,
        });
        fetchDepositRequests();
        fetchAndSetUser(dispatch);
      } finally {
        setActionLoading(false);
        actionLoadingRef.current = false;
      }
    },
    [fetchDepositRequests, dispatch]
  );

  useEffect(() => {
    handleDepositActionRef.current = handleDepositAction;
  }, [handleDepositAction]);

  const columns = useMemo(
    () => [
      {
        title: "ID",
        data: "display_id",
        className: "all",
        responsivePriority: 1,
        render: (d, type) => {
          const label = formatDisplayId(d);
          if (type === "sort" || type === "type") return d ?? 0;
          if (type === "display") {
            return `<div class="seq-cell d-flex align-items-center">
              <span class="deposit-id-label">${escapeHtml(label)}</span>
              <button type="button" class="toggle-row toggle-closed" style="display:none;">+</button>
            </div>`;
          }
          return label;
        },
      },
      {
        title: "Date",
        data: "date",
        responsivePriority: 5,
        render: (d, type) => {
          if (type === "sort" || type === "type") return d ? new Date(d).getTime() : 0;
          return d ? new Date(d).toLocaleString() : "";
        },
      },
      {
        title: "User",
        data: "user",
        responsivePriority: 2,
        render: (d, type) => {
          const value = d || "";
          return type === "display" ? escapeHtml(value) : value;
        },
      },
      {
        title: "Amount",
        data: "amount",
        responsivePriority: 3,
        render: (d, type) => {
          const num = Number(d) || 0;
          if (type === "sort" || type === "type" || type === "filter") return num;
          return num.toLocaleString();
        },
      },
      {
        title: "Payment Type",
        data: "payment_type",
        responsivePriority: 6,
        render: (d, type) => {
          const value = d || "";
          return type === "display" ? escapeHtml(value) : value;
        },
      },
      {
        title: "Transaction Id",
        data: "transaction_id",
        responsivePriority: 7,
        render: (d, type) => {
          const value = d || "";
          return type === "display" ? escapeHtml(value) : value;
        },
      },
      {
        title: "Action",
        data: null,
        orderable: false,
        searchable: false,
        className: "no-export all",
        responsivePriority: 1,
        defaultContent: "",
        render: (_data, type, row) => {
          if (type !== "display") return "";
          const disabled = actionLoadingRef.current ? "disabled" : "";
          const parts = [];
          if (row.image_path) {
            const href = escapeHtml(getMediaUrl(row.image_path));
            parts.push(
              `<a href="${href}" target="_blank" rel="noopener noreferrer" class="btn btn-sm btn-info site_family mr-1">View</a>`
            );
          }
          if (row.status === 0) {
            parts.push(
              `<button type="button" class="btn btn-sm btn-success site_family mr-1 dt-approve" data-id="${escapeHtml(
                String(row.id ?? "")
              )}" style="background:#02b181;border-color:#02b181" ${disabled}>Approve</button>`
            );
            parts.push(
              `<button type="button" class="btn btn-sm btn-danger site_family dt-reject" data-id="${escapeHtml(
                String(row.id ?? "")
              )}" ${disabled}>Cancel</button>`
            );
          }
          if (!parts.length) return "-";
          return `<div class="dt-action-cell">${parts.join("")}</div>`;
        },
      },
    ],
    []
  );

  useEffect(() => {
    const el = tableRef.current;
    if (!el) return;

    const $table = $(el);
    if ($.fn.dataTable.isDataTable($table)) {
      $table.off("click", "button.dt-approve");
      $table.off("click", "button.dt-reject");
      $table.off("click", "button.toggle-row");
      try {
        $table.DataTable().destroy();
      } catch (error) {
        console.warn("Error destroying deposit DataTable:", error);
      }
    }

    const defaultExpand = true;

    let dt;
    try {
      dt = $table.DataTable({
      data: records,
      columns,
      destroy: true,
      autoWidth: false,
      ordering: true,
      order: [[1, "desc"]],
      pageLength: 10,
      lengthMenu: [10, 25, 50, 100],
      lengthChange: true,
      searching: true,
      paging: true,
      info: true,
      orderClasses: false,
      responsive: { details: false },
      language: {
        emptyTable: "No data available in table",
        zeroRecords: "No matching records found",
        lengthMenu: "Show _MENU_ entries",
        search: "Search:",
        info: "Showing _START_ to _END_ of _TOTAL_ entries",
        infoEmpty: "Showing 0 to 0 of 0 entries",
        infoFiltered: "(filtered from _MAX_ total entries)",
        paginate: { previous: "Previous", next: "Next" },
      },
      dom:
        '<"row mb-2 align-items-center deposit-dt-toolbar"' +
        '<"col-sm-12 col-md-4"l>' +
        '<"col-sm-12 col-md-4 text-center"B>' +
        '<"col-sm-12 col-md-4"f>>' +
        "rt" +
        '<"row mt-2 align-items-center"' +
        '<"col-sm-12 col-md-5"i>' +
        '<"col-sm-12 col-md-7"p>>',
      buttons: [
        {
          extend: "print",
          text: "Print",
          className: "btn btn-secondary btn-sm site_family",
          exportOptions: { columns: EXPORT_COLUMNS },
          title: activeTabConfig.listTitle,
        },
        {
          extend: "excelHtml5",
          text: "Excel",
          className: "btn btn-secondary btn-sm site_family",
          exportOptions: { columns: EXPORT_COLUMNS },
          title: activeTabConfig.listTitle,
        },
        {
          extend: "pdfHtml5",
          text: "PDF",
          className: "btn btn-secondary btn-sm site_family",
          orientation: "landscape",
          pageSize: "A4",
          exportOptions: { columns: EXPORT_COLUMNS },
          title: activeTabConfig.listTitle,
        },
      ],
      rowCallback: function (row, _data, index) {
        const bg = index % 2 === 0 ? "#f7f7f7" : "#ffffff";
        $(row).css("background-color", bg);
        $("td", row).css("background-color", bg);
      },
    });
    } catch (error) {
      console.error("Error initializing deposit DataTable:", error);
      return;
    }

    const getHiddenColumnsForRow = (tr) => {
      const hiddenCols = [];
      $(tr)
        .find("td")
        .each((i, td) => {
          if ($(td).css("display") === "none" || $(td).is(":hidden")) {
            hiddenCols.push(i);
          }
        });
      return hiddenCols;
    };

    const buildHiddenDetails = (tr) => {
      const hiddenCols = getHiddenColumnsForRow(tr);
      if (!hiddenCols.length || hiddenCols.length === columns.length) return "";

      let html = "";
      hiddenCols.forEach((i) => {
        const col = dt.column(i);
        if (!col || !col.header()) return;
        const title = ($(col.header()).text() || "").trim();
        if (!title) return;
        const val = $(tr).find("td").eq(i).html() || "";
        html += `
          <tr>
            <td class="deposit-detail-cell">
              <div class="deposit-detail-line">
                <span class="deposit-detail-label">${escapeHtml(title)}:</span>
                <div class="deposit-detail-value">${val}</div>
              </div>
            </td>
          </tr>`;
      });
      return html;
    };

    const updateToggleButtons = () => {
      if (!$.fn.dataTable.isDataTable($table)) return;
      dt.rows().every(function () {
        const tr = $(this.node());
        const $btn = tr.find("button.toggle-row");
        const hiddenCols = getHiddenColumnsForRow(tr);
        const anyHidden =
          hiddenCols.length > 0 && hiddenCols.length < columns.length;

        if (anyHidden) {
          $btn.show();
          if (defaultExpand && !this.child.isShown()) {
            const details = buildHiddenDetails(tr);
            if (details.trim()) {
              this.child(
                `<table class="table table-sm mb-0 deposit-child-table">${details}</table>`
              ).show();
              tr.addClass("shown");
              $btn.text("−").removeClass("toggle-closed").addClass("toggle-open");
            }
          } else if (this.child.isShown()) {
            const newDetails = buildHiddenDetails(tr);
            this.child(
              `<table class="table table-sm mb-0 deposit-child-table">${newDetails}</table>`
            ).show();
          }
        } else {
          $btn.hide();
          if (this.child && this.child.isShown()) {
            this.child.hide();
            tr.removeClass("shown");
            $btn.text("+").removeClass("toggle-open").addClass("toggle-closed");
          }
        }
      });
    };

    $table.on("click", "button.toggle-row", function (event) {
      event.preventDefault();
      event.stopPropagation();
      const tr = $(this).closest("tr");
      const row = dt.row(tr);
      const hiddenCols = getHiddenColumnsForRow(tr);
      if (!hiddenCols.length || hiddenCols.length === columns.length) return;
      if (row.child.isShown()) {
        row.child.hide();
        tr.removeClass("shown");
        $(this).text("+").removeClass("toggle-open").addClass("toggle-closed");
      } else {
        const details = buildHiddenDetails(tr);
        if (!details.trim()) return;
        row
          .child(`<table class="table table-sm mb-0 deposit-child-table">${details}</table>`)
          .show();
        tr.addClass("shown");
        $(this).text("−").removeClass("toggle-closed").addClass("toggle-open");
      }
    });

    dt.on("order.dt search.dt draw.dt", () => {
      try {
        updateToggleButtons();
      } catch (e) {
        console.warn("Toggle update failed:", e);
      }
    });

    requestAnimationFrame(() => updateToggleButtons());

    const safeRecalc = () => {
      if (!$.fn.dataTable.isDataTable($table)) return;
      clearTimeout(window.__deposit_dt_timer);
      window.__deposit_dt_timer = setTimeout(() => {
        try {
          if (dt.responsive) dt.responsive.recalc();
          requestAnimationFrame(() => updateToggleButtons());
        } catch (err) {
          console.warn("Resize skip:", err);
        }
      }, 30);
    };

    dt.on("responsive-resize draw", safeRecalc);
    window.addEventListener("resize", safeRecalc);

    const onActionClick = (action) =>
      function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (actionLoadingRef.current) return;
        const id = $(this).attr("data-id");
        if (id && handleDepositActionRef.current) {
          handleDepositActionRef.current(id, action);
        }
      };

    $table.on("click", "button.dt-approve", onActionClick("approve"));
    $table.on("click", "button.dt-reject", onActionClick("reject"));

    return () => {
      window.removeEventListener("resize", safeRecalc);
      clearTimeout(window.__deposit_dt_timer);
      $table.off("click", "button.dt-approve");
      $table.off("click", "button.dt-reject");
      $table.off("click", "button.toggle-row");
      if ($.fn.dataTable.isDataTable($table)) {
        try {
          dt.off("order.dt search.dt draw.dt");
          dt.off("responsive-resize draw");
          dt.destroy();
        } catch (error) {
          console.warn("Error during deposit DataTable cleanup:", error);
        }
      }
    };
  }, [records, columns, gridKey, activeTabConfig.listTitle]);

  useEffect(() => {
    actionLoadingRef.current = actionLoading;
    const el = tableRef.current;
    if (!el) return;
    $(el)
      .find("button.dt-approve, button.dt-reject")
      .prop("disabled", actionLoading);
  }, [actionLoading]);

  return (
    <AgentLayout>
      <div className="container-fluid">
        <style>{`
          .deposit-request-dt .dataTables_wrapper .dataTables_filter {
            text-align: right;
          }
          .deposit-request-dt .dataTables_wrapper .dataTables_filter input {
            margin-left: 0.5em;
            display: inline-block;
            width: auto;
          }
          .deposit-request-dt .dataTables_wrapper .dataTables_length select {
            display: inline-block;
            width: auto;
          }
          .deposit-request-dt .dt-buttons {
            display: inline-flex;
            justify-content: center;
            flex-wrap: wrap;
            gap: 4px;
          }
          .deposit-request-dt .dt-button,
          .deposit-request-dt button.dt-button {
            color: #fff !important;
            background-color: #6c757d !important;
            background-image: none !important;
            border: 1px solid #6c757d !important;
            border-radius: 0.2rem;
            padding: 0.25rem 0.5rem;
            margin: 0 2px;
            font-size: 0.875rem;
            line-height: 1.5;
          }
          .deposit-request-dt .dt-button:hover,
          .deposit-request-dt button.dt-button:hover {
            background-color: #5a6268 !important;
            border-color: #545b62 !important;
          }
          .deposit-request-dt .page-item.active .page-link {
            z-index: 1;
            color: #fff;
            background-color: #0ab180;
            border-color: #20a8d8;
          }
          .deposit-request-dt .dt-action-cell {
            white-space: nowrap;
          }
          .deposit-request-dt table.dataTable {
            width: 100% !important;
          }
          .deposit-request-dt .dataTables_wrapper,
          .deposit-request-dt .dataTables_scroll {
            overflow-x: hidden !important;
          }
          .deposit-request-dt .seq-cell {
            display: flex;
            align-items: center;
            gap: 6px;
            flex-wrap: nowrap;
          }
          .deposit-request-dt .toggle-row {
            border: none;
            outline: none;
            cursor: pointer;
            width: 18px;
            height: 18px;
            margin-left: 2px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            font-weight: 700;
            font-size: 14px;
            color: #fff;
            line-height: 1;
            padding: 0;
            box-shadow: none;
            -webkit-tap-highlight-color: transparent;
            flex-shrink: 0;
          }
          .deposit-request-dt .toggle-row:focus,
          .deposit-request-dt .toggle-row:focus-visible {
            outline: none !important;
            box-shadow: none !important;
          }
          .deposit-request-dt .toggle-closed { background-color: #28a745; }
          .deposit-request-dt .toggle-open { background-color: #dc3545; }
          .deposit-request-dt .toggle-row:active { transform: scale(0.94); }
          .deposit-request-dt table.dataTable.dtr-inline.collapsed > tbody > tr > td:first-child:before,
          .deposit-request-dt table.dataTable.dtr-inline.collapsed > tbody > tr > th:first-child:before,
          .deposit-request-dt table.dataTable.dtr-inline.collapsed > tbody > tr.parent > td:first-child:before,
          .deposit-request-dt table.dataTable.dtr-inline.collapsed > tbody > tr.parent > th:first-child:before {
            display: none !important;
            content: none !important;
          }
          .deposit-request-dt table.dataTable.dtr-inline.collapsed > tbody > tr > td:first-child,
          .deposit-request-dt table.dataTable.dtr-inline.collapsed > tbody > tr > th:first-child {
            padding-left: 8px !important;
            cursor: default;
          }
          .deposit-request-dt .deposit-child-table {
            width: 100%;
            margin: 0;
            background: #fff;
          }
          .deposit-request-dt .deposit-detail-cell {
            border: none;
            border-bottom: 1px solid #eee;
            padding: 6px 8px;
          }
          .deposit-request-dt .deposit-detail-line {
            display: flex;
            align-items: flex-start;
            gap: 8px;
            flex-wrap: wrap;
          }
          .deposit-request-dt .deposit-detail-label {
            font-weight: bold;
            white-space: nowrap;
          }
          .deposit-request-dt tr.child,
          .deposit-request-dt tr.child td {
            background-color: #ffffff !important;
          }
          @media screen and (max-width: 767px) {
            .deposit-request-dt .dt-action-cell {
              display: flex;
              flex-direction: column;
              align-items: stretch;
              white-space: normal;
              width: 100%;
            }
            .deposit-request-dt .dt-action-cell > .btn,
            .deposit-request-dt .dt-action-cell > a.btn {
              width: 100%;
              height: 28px;
              margin: 0 !important;
              margin-bottom: 2px !important;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              box-sizing: border-box;
              padding: 0 8px;
              font-size: 12px;
              line-height: 1;
            }
            .deposit-request-dt .dt-action-cell > .btn:last-child,
            .deposit-request-dt .dt-action-cell > a.btn:last-child {
              margin-bottom: 0 !important;
            }
            .deposit-request-dt .dataTables_wrapper .dataTables_filter,
            .deposit-request-dt .dataTables_wrapper .dataTables_info,
            .deposit-request-dt .dt-buttons {
              text-align: center;
              float: none;
              width: 100%;
              margin-top: 8px;
            }
            .deposit-request-dt .dataTables_wrapper .dataTables_paginate {
              float: none;
              text-align: center;
            }
          }
        `}</style>
        <div className="bets-section-wrapper">
          <NavButton />

          <div className="card-body reportmenubuttons" style={{ padding: "0 0 10px 0" }}>
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.status}
                type="button"
                className="btn btn_top site_family"
                style={{
                  background: activeStatus === tab.status ? "#02b181" : "#00b181",
                  color: "#fff",
                  borderColor: "#28a745",
                  marginRight: "5px",
                  marginBottom: "5px",
                  opacity: activeStatus === tab.status ? 1 : 0.85,
                }}
                onClick={() => setActiveStatus(tab.status)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <i className="fa fa-align-justify"></i>
                  <strong className="site_family">{activeTabConfig.filterTitle}</strong>
                </div>
                <div className="card-body" style={{ padding: "0.8rem 1.25rem" }}>
                  <form className="form-inline" onSubmit={(e) => e.preventDefault()}>
                    <div className="row" style={{ textAlignLast: "justify", width: "100%" }}>
                      <div className="col-12 col-md-5">
                        <Bootstrap4DatePickerComponent
                          pickerId="DepositFrom"
                          inputId="DepositDisplayFrom"
                          defaultDate={startDate}
                          setSelectedDate={setStartDate}
                        />
                      </div>
                      <strong style={{ margin: "auto" }}>&nbsp;-&nbsp;</strong>
                      <div className="col-12 col-md-5">
                        <Bootstrap4DatePickerComponent
                          pickerId="DepositTo"
                          inputId="DepositDisplayTo"
                          defaultDate={endDate}
                          setSelectedDate={setEndDate}
                        />
                      </div>
                      <div className="form-group editsbmtbtn">
                        <button
                          type="button"
                          className="btn btn-success site_family"
                          style={{ background: "#02b181" }}
                          onClick={() => {
                            setMessage({ text: "", isError: false });
                            fetchDepositRequests();
                          }}
                          disabled={isLoading || actionLoading}
                        >
                          <strong>Submit</strong>
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {message.text && (
            <div
              className={`alert ${message.isError ? "alert-danger" : "alert-success"} site_family`}
              style={{ marginTop: "10px" }}
            >
              {message.text}
            </div>
          )}

          <div className="row" style={{ marginTop: "10px" }}>
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <i className="fa fa-align-justify"></i>
                  <strong className="site_family">{activeTabConfig.listTitle}</strong>
                </div>
                <div className="card-body deposit-request-dt">
                  {isLoading && (
                    <div className="text-center mb-2">
                      <div className="spinner-border spinner-border-sm text-success" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                    </div>
                  )}
                  <div key={gridKey}>
                    <table
                      ref={tableRef}
                      id="depositRequestTable"
                      className="table table-bordered table-striped table-sm site_family display"
                      style={{ width: "100%" }}
                    >
                      <thead>
                        <tr>
                          {columns.map((col) => (
                            <th key={col.title}>{col.title}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody />
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AgentLayout>
  );
};

export default DepositRequest;
